import React from 'react';
import {
  Flame, Compass, Filter, Wand2, Clapperboard, Users, Timer, ShieldAlert, BarChart3, Workflow,
} from 'lucide-react';
import { GuidaLayout, Blocco, Passi, L, A } from '../components/GuidaUI';

/**
 * Guida ai trend per SMM e videomaker: cosa sono, dove trovarli, come sceglierli,
 * adattarli, girarli e misurarli — e come si lavora insieme con la tab Trend.
 */

const SEZIONI = [
  { id: 'cosa', label: 'Cos\'è un trend', Icon: Flame },
  { id: 'dove', label: 'Dove trovarli', Icon: Compass },
  { id: 'scegliere', label: 'Quali scegliere', Icon: Filter },
  { id: 'adattare', label: 'Come adattarli', Icon: Wand2 },
  { id: 'girare', label: 'Per il videomaker', Icon: Clapperboard },
  { id: 'team', label: 'Come lavoriamo', Icon: Workflow },
  { id: 'tempi', label: 'Tempi', Icon: Timer },
  { id: 'rischi', label: 'Musica e rischi', Icon: ShieldAlert },
  { id: 'misurare', label: 'Ha funzionato?', Icon: BarChart3 },
];

const TIPI = [
  ['Format', 'Una struttura riconoscibile: "POV…", "3 cose che non sapevi su…", prima/dopo, "preparati con me", transizioni.'],
  ['Audio', 'Un suono o una canzone che in tanti usano in quel momento, spesso con un gesto o un momento preciso sul beat.'],
  ['Tema', 'Un argomento di cui parlano tutti: una ricorrenza, un evento, una notizia leggera, la stagione.'],
  ['Estetica', 'Uno stile visivo: colori, luce, montaggio, inquadrature, font del testo a schermo.'],
  ['Meme', 'Una battuta o un\'immagine che gira e che si può rigirare sul proprio settore.'],
];

const FONTI = [
  ['TikTok Creative Center', 'https://ads.tiktok.com/business/creativecenter',
    'La fonte ufficiale di TikTok: sezione Trend con hashtag, canzoni, creator e video in crescita. Scegli il paese (Italia) e il periodo. Per le canzoni guarda quelle "approvate per uso commerciale".'],
  ['TikTok e Instagram, nell\'app', null,
    'Scorri il feed "Per te" / Reels con un account di lavoro che segue il settore dei clienti. Su Instagram, nei Reel, una freccia ↗ accanto al nome dell\'audio indica che è in crescita; toccando l\'audio vedi quanti Reel lo usano.'],
  ['YouTube Shorts', null,
    'Il feed Shorts e i suoni: da uno Short puoi aprire il suono e vedere gli altri video che lo usano.'],
  ['Google Trends', 'https://trends.google.it',
    '"Di tendenza ora" con paese Italia: cosa cercano le persone in questo momento. Utile per i temi, non per i format.'],
  ['Pinterest Trends', 'https://trends.pinterest.com',
    'Ottimo per estetica, moda, gioielli, casa e ricorrenze: mostra cosa cresce in anticipo rispetto agli altri social.'],
  ['Competitor e creator del settore', null,
    'Una lista di account (competitor dei clienti, creator del settore, brand che stimiamo) da guardare ogni giorno. Quando un loro contenuto fa molto più del solito, è un segnale.'],
  ['Calendario e ricorrenze', null,
    'Natale, San Valentino, Festa della mamma, Black Friday, rientro a settembre, eventi come Sanremo: si sanno in anticipo, quindi si preparano in anticipo.'],
  ['Commenti e messaggi dei clienti', null,
    'Le domande che ripetono i follower dei clienti sono idee pronte: spesso basta un format di tendenza per rispondere.'],
];

const DOMANDE = [
  'C\'entra con il cliente e con la sua Voce del brand? Se va forzato, lascia stare.',
  'Riusciamo a farlo bene in 2–3 giorni? Un trend fatto tardi vale poco.',
  'L\'audio si può usare con un profilo aziendale? (vedi "Musica e rischi")',
  'Siamo ancora in tempo? Se lo stanno già facendo tutti i brand, probabilmente è tardi.',
  'Il cliente sarebbe a suo agio a vederlo sul suo profilo?',
  'C\'è qualche rischio: politica, tragedie, prese in giro di persone, sfide pericolose?',
];

export default function GuidaTrend() {
  return (
    <GuidaLayout Icon={Flame} titolo="Guida ai trend" sezioni={SEZIONI}
      sottotitolo="Per social media manager e videomaker: trovare i trend giusti, adattarli ai clienti e farli in tempo."
      azioni={<L to="/trend">Apri la bacheca Trend →</L>}>

      <Blocco id="cosa" Icon={Flame} titolo="Cos'è un trend" sotto="Qualcosa che in tanti stanno facendo adesso e che il pubblico riconosce al volo.">
        <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'minmax(80px, 110px) 1fr', gap: '10px 16px' }}>
          {TIPI.map(([t, d]) => (
            <React.Fragment key={t}>
              <dt style={{ fontWeight: 700, color: 'var(--text)' }}>{t}</dt>
              <dd style={{ margin: 0 }}>{d}</dd>
            </React.Fragment>
          ))}
        </dl>
        <p style={{ margin: '14px 0 0' }}>
          Un trend funziona perché è <b>familiare</b>: chi guarda riconosce la struttura e si concentra sulla variazione.
          Il nostro lavoro è mettere il cliente dentro quella struttura, non copiare il video.
        </p>
      </Blocco>

      <Blocco id="dove" Icon={Compass} titolo="Dove trovarli" sotto="Le fonti da controllare, dalla più utile.">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {FONTI.map(([nome, url, d]) => (
            <div key={nome} style={{ padding: '12px 14px', borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg2)' }}>
              <div style={{ fontSize: 14, fontWeight: 650, color: 'var(--text)' }}>{url ? <A href={url}>{nome} ↗</A> : nome}</div>
              <div style={{ fontSize: 13, marginTop: 3 }}>{d}</div>
            </div>
          ))}
        </div>
        <p style={{ margin: '14px 0 0' }}>
          Consiglio pratico: un <b>account di lavoro</b> per piattaforma che segue solo i settori dei nostri clienti. L'algoritmo
          impara in fretta e il feed diventa la fonte di trend più veloce che abbiamo.
        </p>
      </Blocco>

      <Blocco id="scegliere" Icon={Filter} titolo="Quali scegliere" sotto="Sei domande prima di dire sì. Se una risposta è no, si passa al prossimo.">
        <Passi items={DOMANDE} />
      </Blocco>

      <Blocco id="adattare" Icon={Wand2} titolo="Come adattarli" sotto="La formula: struttura del trend + prodotto o servizio del cliente + la sua voce.">
        <Passi items={[
          <>Scomponi il trend: <b>hook</b> (cosa succede nei primi 1–2 secondi), <b>struttura</b> (quante scene, dove cade il colpo di scena), <b>testo a schermo</b>, <b>audio</b>.</>,
          <>Tieni fisso quello che lo rende riconoscibile (struttura, ritmo, audio) e cambia il contenuto con quello del cliente.</>,
          <>Scrivi l'hook pensando a chi non conosce il brand: deve funzionare anche senza audio.</>,
          <>Controlla la <b>Voce del brand</b> del cliente (Piano Editoriale → ⋯ → Voce del brand): tono e parole da evitare valgono anche per i trend.</>,
          <>Prepara una <b>caption</b> breve che aggiunge qualcosa (un consiglio, una domanda), non che spiega la battuta.</>,
        ]} />
        <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
          {[
            ['Gioielleria', '"POV: entri per un regalo ed esci con due" — trend POV + pezzi del negozio.'],
            ['Concessionaria', '"3 cose che controlli prima di comprare un\'auto usata" — format lista + volti del team.'],
            ['Palestra', 'Prima/dopo di un allenamento sul beat di un audio in crescita.'],
          ].map(([t, d]) => (
            <div key={t} style={{ padding: '10px 12px', borderRadius: 10, background: 'var(--bg2)', border: '1px solid var(--border)', fontSize: 13 }}>
              <b style={{ color: 'var(--text)' }}>{t}</b><br />{d}
            </div>
          ))}
        </div>
      </Blocco>

      <Blocco id="girare" Icon={Clapperboard} titolo="Per il videomaker" sotto="Come trasformare un trend in un video pronto in poco tempo.">
        <Passi items={[
          <>Apri il trend nella bacheca <L to="/trend">Trend</L>: il video scaricato si guarda fotogramma per fotogramma. Conta scene e secondi, segna dove cadono i tagli sul beat.</>,
          <>Fai una <b>lista delle inquadrature</b> prima di girare: per un trend bastano spesso 3–6 riprese.</>,
          <>Gira in <b>verticale 9:16</b> (1080×1920), con luce buona e più ciak per ogni ripresa: al montaggio servono margini.</>,
          <>Tieni testo e dettagli importanti lontani dai bordi, soprattutto in basso e a destra, dove l'app copre con nome, caption e pulsanti.</>,
          <>Monta sul beat, metti il testo a schermo leggibile e i sottotitoli: molti guardano senza audio.</>,
          <>Se il trend torna spesso, salva il progetto o un modello di montaggio: la volta dopo ci metti la metà.</>,
          <>Carica il montaggio nel contenuto (sezione <b>File e media</b> o come versione in Produzione video) così il social media manager lo rivede subito.</>,
        ]} />
      </Blocco>

      <Blocco id="team" Icon={Users} titolo="Come lavoriamo insieme" sotto="Dal trend visto al contenuto online, tutto dentro EdenFlow.">
        <Passi items={[
          <><b>Chiunque</b> veda un trend lo incolla nella bacheca <L to="/trend">Trend</L>: basta il link (o trascinare il video). Il server prova a scaricarlo da solo.</>,
          <>Aggiungi due righe: <b>perché funziona</b> e <b>come lo usiamo</b> (per quale cliente). Senza questo, è solo un link.</>,
          <>Il team vota con 🔥 quelli che vale la pena fare. Il social media manager li sposta in <b>Da provare</b>.</>,
          <>Quando si decide di farlo: nel trend, <b>Usalo per un cliente → Crea contenuto</b>. Nasce un contenuto "Idea" nel Piano Editoriale, già collegato al trend; il trend diventa "Usato".</>,
          <>Da lì il percorso è quello di sempre: copy, visual, file, approvazione, programmazione (il <b>Prossimo passo</b> nella scheda ti guida).</>,
          <>Un trend che non si fa più in tempo va segnato <b>Scaduto</b>: la bacheca resta pulita.</>,
        ]} />
        <p style={{ margin: '14px 0 0' }}>
          Se il video non si scarica (alcune piattaforme chiedono il login), il trend resta salvato con il link:
          apri l'originale, salva il video dal telefono e allegalo con <b>Allega file</b>.
        </p>
      </Blocco>

      <Blocco id="tempi" Icon={Timer} titolo="Tempi" sotto="La velocità conta più della perfezione.">
        <ul style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <li>Molti trend legati a un audio durano da pochi giorni a qualche settimana: l'obiettivo è essere online <b>entro 2–3 giorni</b> da quando lo vediamo.</li>
          <li>Il momento migliore è quando sta <b>crescendo</b>: lo vedi spesso nel feed ma i brand del settore non l'hanno ancora fatto.</li>
          <li>Nel Piano Editoriale lascia <b>uno o due posti liberi a settimana</b> per i trend: così non si stravolge il piano ogni volta.</li>
          <li>Per un trend, l'approvazione del cliente deve essere veloce: avvisalo prima che arriverà un contenuto "al volo".</li>
        </ul>
      </Blocco>

      <Blocco id="rischi" Icon={ShieldAlert} titolo="Musica, diritti e rischi">
        <ul style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <li><b>Profili aziendali e musica</b>: su Instagram/Facebook e TikTok i profili business possono usare solo la musica delle librerie commerciali delle piattaforme. Se l'audio di tendenza non c'è, rifai il format con un audio commerciale simile o con la voce.</li>
          <li>I video che scarichiamo servono come <b>riferimento interno</b>: non si ripubblicano mai contenuti di altri.</li>
          <li>Niente video con il logo di un'altra app (es. il watermark TikTok su un Reel): si esporta sempre il file pulito dal montaggio.</li>
          <li>Prima di usare un meme o una frase, controlla da dove nasce e cosa significa: alcuni hanno origini che un brand non vuole.</li>
          <li>Mai trend su tragedie, politica, sfide pericolose o che prendono in giro persone reali.</li>
        </ul>
      </Blocco>

      <Blocco id="misurare" Icon={BarChart3} titolo="Ha funzionato?">
        <Passi items={[
          <>Dopo 2 giorni e dopo una settimana confronta il contenuto con la media del cliente: copertura, salvataggi e condivisioni contano più dei like. I numeri li trovi in <L to="/pubblicazioni">Pubblicazioni</L>.</>,
          <>Se è andato bene, scrivilo nel trend (in "Perché funziona") e valuta una seconda versione: i format che funzionano si ripetono.</>,
          <>Se è andato male, chiediti se era tardi, se l'hook era debole o se il trend non c'entrava con il cliente: è utile quanto un successo.</>,
        ]} />
      </Blocco>
    </GuidaLayout>
  );
}
